/**
 * OddsChart — Visual odds comparison for a single game/market
 * Pure CSS bar chart — no external chart library dependency
 *
 * Props:
 *   game      — A game object with .bookmakers[].markets[].outcomes[]
 *   marketKey — Which market to plot (e.g. 'h2h', 'spreads', 'totals')
 */

import { formatBookmakerName, formatMarketName } from '../../lib/oddsFormatters'
import './OddsChart.css'

const COLORS = ['#4CAF50', '#2196F3', '#FFC107', '#FF5722', '#9C27B0', '#00BCD4', '#E91E63']

function impliedProbability(odds) {
  if (odds > 0) return (100 / (odds + 100)) * 100
  return (Math.abs(odds) / (Math.abs(odds) + 100)) * 100
}

function formatOddsStr(odds) {
  return odds > 0 ? `+${odds}` : `${odds}`
}

function OddsChart({ game, marketKey }) {
  if (!game || !game.bookmakers || game.bookmakers.length === 0) {
    return <div className="oc-empty">No odds data available</div>
  }

  // Collect outcomes per bookmaker for this market
  const rows = []
  game.bookmakers.forEach((bookmaker) => {
    const market = bookmaker.markets.find(m => m.key === marketKey)
    if (!market) return

    const bmName = formatBookmakerName(bookmaker.key) || bookmaker.title || bookmaker.key
    market.outcomes.forEach(outcome => {
      rows.push({
        bookmaker: bmName,
        team: outcome.name,
        price: outcome.price,
        point: outcome.point,
      })
    })
  })

  if (rows.length === 0) {
    return <div className="oc-empty">No {formatMarketName(marketKey)} odds for this game</div>
  }

  // Get unique teams for color assignment
  const teams = [...new Set(rows.map(r => r.team))]
  const teamColor = {}
  teams.forEach((t, i) => { teamColor[t] = COLORS[i % COLORS.length] })

  // Get unique bookmakers preserving order
  const bookmakers = [...new Set(rows.map(r => r.bookmaker))]

  // Find range for bar sizing
  const allOdds = rows.map(r => r.price)
  const minOdds = Math.min(...allOdds)
  const maxOdds = Math.max(...allOdds)
  const range = Math.max(maxOdds - minOdds, 50) // at least 50 range

  // Normalize odds to a 0-100% width for bar display
  const normalize = (odds) => {
    return Math.max(15, ((odds - minOdds) / range) * 80 + 15)
  }

  const title = `${game.away_team} @ ${game.home_team} — ${formatMarketName(marketKey)}`

  return (
    <div className="oc-container">
      <div className="oc-title">{title}</div>
      {/* Legend */}
      <div className="oc-legend">
        {teams.map(t => (
          <span key={t} className="oc-legend-item">
            <span className="oc-legend-dot" style={{ background: teamColor[t] }} />
            {t}
          </span>
        ))}
      </div>
      {/* Bar chart rows grouped by bookmaker */}
      <div className="oc-bars">
        {bookmakers.map(bm => {
          const bmRows = rows.filter(r => r.bookmaker === bm)
          return (
            <div key={bm} className="oc-bm-group">
              <div className="oc-bm-name">{bm}</div>
              <div className="oc-bm-bars">
                {bmRows.map((r, i) => (
                  <div key={i} className="oc-bar-row">
                    <div
                      className="oc-bar"
                      style={{
                        width: `${normalize(r.price)}%`,
                        background: teamColor[r.team],
                      }}
                      title={`${r.team}: ${formatOddsStr(r.price)} (${impliedProbability(r.price).toFixed(1)}%)${r.point != null ? ` Line: ${r.point > 0 ? '+' : ''}${r.point}` : ''}`}
                    >
                      <span className="oc-bar-label">{formatOddsStr(r.price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OddsChart
